import { CommonModule } from '@angular/common';
import { Component, forwardRef, Inject, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Automation, ErrorHandlingType, IsAutomationDraining, RunType } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { Observable } from 'rxjs';
import { AutomationService } from '../../automation/automation.service';
import { EntityPipe } from '../../shared/entity.pipe';
import { AutomationsRouterService } from '../../shared/automations-router.service';
import { DialogData } from './dialog-data';
import { DialogResult } from './dialog-result';

export const RunTypeOptions = [
  {
    label: 'AUTOMATIONS.SETTINGS.RUN_OPTIONS.RUN_ONCE_ENTITY',
    value: RunType.RUN_TYPE_ONLY_ONCE,
  },
  {
    label: 'AUTOMATIONS.SETTINGS.RUN_OPTIONS.RUN_MULTIPLE_ENTITY',
    value: RunType.RUN_TYPE_MULTIPLE,
  },
  {
    label: 'AUTOMATIONS.SETTINGS.RUN_OPTIONS.RUN_ONE_AT_A_TIME',
    value: RunType.RUN_TYPE_ONE_AT_A_TIME,
    hint: 'AUTOMATIONS.SETTINGS.RUN_OPTIONS.RUN_ONE_AT_A_TIME_HINT',
  },
];

export const ErrorHandlingOptions = [
  {
    label: 'AUTOMATIONS.SETTINGS.ERROR_HANDLING_OPTIONS.SKIP',
    value: ErrorHandlingType.ERROR_HANDLING_TYPE_CONTINUE_ON_ERROR,
  },
  {
    label: 'AUTOMATIONS.SETTINGS.ERROR_HANDLING_OPTIONS.STOP_RUN',
    hint: 'AUTOMATIONS.SETTINGS.ERROR_HANDLING_OPTIONS.STOP_HINT',
    value: ErrorHandlingType.ERROR_HANDLING_TYPE_STOP_ON_ERROR,
  },
];

@Component({
  templateUrl: './turn-on-automation-dialog.component.html',
  styleUrls: ['./turn-on-automation-dialog.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    GalaxyAlertModule,
    MatRadioModule,
    MatFormFieldModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    EntityPipe,
    GalaxyPipesModule,
    MatCheckboxModule,
    GalaxyFormFieldModule,
  ],
})
export class TurnOnAutomationDialogComponent implements OnInit {
  automation: Automation;
  runTypeOptions = RunTypeOptions;
  errorHandlingTypeOptions = ErrorHandlingOptions;
  form: UntypedFormGroup;

  originBuiltInAutomation$: Observable<Automation>;
  originBuiltInAutomationOn$: Observable<boolean>;

  subscriptionFormControl = new UntypedFormControl(false);

  constructor(
    public dialogRef: MatDialogRef<TurnOnAutomationDialogComponent, DialogResult>,
    private readonly router: AutomationsRouterService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly translate: TranslateService,
    private readonly fb: UntypedFormBuilder,
    @Inject(forwardRef(() => AutomationService)) private readonly automationService: AutomationService,
  ) {
    this.automation = this.data.automation;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      runType: [this.automation.settings.runType],
      errorHandlingType: [this.automation.settings.errorHandlingType],
    });

    if (this.data.automation.originDefaultAutomationId) {
      const automation = this.data.automation;
      this.originBuiltInAutomationOn$ = this.automationService.originBuiltInAutomationIsOn(
        automation.namespace,
        automation.context,
        automation.originDefaultAutomationId,
      );
      this.originBuiltInAutomation$ = this.automationService.getOriginBuiltInAutomation(
        automation.originDefaultAutomationId,
      );
    }
    this.subscriptionFormControl.setValue(true);
  }

  isAutomationDraining(): boolean {
    return IsAutomationDraining(this.automation);
  }

  goToSettings(): void {
    this.router.navigate([`/automations/automation/${this.automation.id}/settings`]);
    this.dialogRef.close(undefined);
  }

  continue(): void {
    const settings = this.automation.settings;
    settings.runType = this.form.controls.runType.value;
    settings.errorHandlingType = this.form.controls.errorHandlingType.value;
    this.dialogRef.close({
      settings: settings,
      automationId: this.automation.id,
      subscribe: this.subscriptionFormControl.value,
    });
  }
}
